import styled, { keyframes, css } from "styled-components";
import logoLapiezaBlue from "../../assets/logo-lapieza-blue.webp";
import { palette } from "../../styles/palette";

export { css };

export const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(239, 142, 2, 0.6); }
  70% { box-shadow: 0 0 0 16px rgba(239, 142, 2, 0); }
  100% { box-shadow: 0 0 0 0 rgba(239, 142, 2, 0.6); }
`;

export const blink = keyframes`
  0% { opacity: 1; }
  12.5% { opacity: 0.3; }
  25% { opacity: 1; }
  37.5% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 1; }
`;

export const RefreshButton = styled.button<{
  $isBlinking: boolean;
  $isPlaying: boolean;
}>`
  align-self: center;
  background: transparent;
  border: none;
  color: ${palette.grenTitle};
  font-size: 16px;
  cursor: ${({ $isPlaying }) => ($isPlaying ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 6px;
  transition: all 0.2s ease;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 0.5 : 1)};
  text-decoration: underline;
  text-decoration-color: ${palette.grenTitle};
  text-underline-offset: 2px;
  font-weight: 500;
  animation: ${({ $isBlinking }) =>
    $isBlinking
      ? css`
          ${blink} 2s ease-in-out
        `
      : "none"};

  &:hover {
    background: ${palette.greenSecond};
    text-decoration: none;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${palette.greenSecond};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-width: 100vw;
  &::before {
    content: "";
    position: absolute;
    top: 20px;
    left: 20px;
    width: 130px;
    height: 80px;
    background: url(${logoLapiezaBlue}) no-repeat left top;
    background-size: contain;
    opacity: 1;
    pointer-events: none;
    z-index: 5000;
  }
`;

export const Card = styled.div`
  background: ${palette.white};
  border-radius: 8px;
  box-shadow: ${palette.boxShadowCard};
  width: 100%;
  max-width: 420px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px 0 20px;
  position: relative;
  border: 1.5px solid ${palette.greenSecond};
`;

export const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isSpeaking",
})<{ isSpeaking: boolean }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: ${({ isSpeaking }) =>
    isSpeaking ? palette.greenMain : palette.greenSecond};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 44px;
  color: ${palette.white};
  margin-bottom: 12px;
  box-shadow: ${({ isSpeaking }) =>
    isSpeaking ? `0 0 0 8px ${palette.greenMainLight}` : "none"};
  transition: background 0.3s;
  ${({ isSpeaking }) =>
    isSpeaking &&
    css`
      animation: ${pulse} 1s infinite;
    `}
  overflow: hidden;
`;

export const Greeting = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${palette.grenTitle};
  margin-bottom: 16px;
  text-align: center;
`;

export const StatusTitle = styled.div`
  font-weight: 700;
  font-size: 18px;
  color: ${palette.textGreenTitle};
  margin-bottom: 2px;
`;

export const StatusSub = styled.div`
  font-size: 14px;
  color: ${palette.secondaryTextColor};
  margin-bottom: 18px;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const CallButton = styled.button`
  background: ${palette.orange} !important;
  color: ${palette.white} !important;
  border: none !important;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${palette.shadowMedium};
  animation: ${pulse} 2s infinite;

  &:hover {
    background: ${palette.orangeTwo} !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${palette.shadowDark};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px ${palette.shadowMedium};
  }

  &:disabled {
    background: ${palette.orange} !important;
    opacity: 0.5;
    cursor: not-allowed;
    animation: none;
    transform: none;
    box-shadow: 0 2px 6px ${palette.shadowLight};
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

export const HangupButton = styled.button`
  background: ${palette.dangerColor};
  color: ${palette.white};
  border: none;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${palette.shadowMedium};

  &:hover {
    background: ${palette.dangerColor};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${palette.shadowDark};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px ${palette.shadowMedium};
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

export const CallTimer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 16px 0 24px 0;
  gap: 12px;
`;

export const TimerDisplay = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${palette.grenTitle};
  text-align: center;
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: ${palette.greenSecond};
  border-radius: 4px;
  overflow: hidden;
`;

export const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  background: ${palette.orange};
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`;

export const CallStatusMessage = styled.p`
  color: ${palette.dangerColor};
  font-weight: 500;
  text-align: center;
  margin-bottom: 18px;
  line-height: 1.6;
`;

export const AvatarImage = styled.img`
  width: 64px;
  height: 64px;
  object-fit: cover;
`;

export const LoadingSpinner = styled.span`
  font-size: 22px;
`;

export const ErrorText = styled.div`
  padding: 40px;
  color: red;
`;
